import {
    useGetTicketingsMutation,
    useGetArchivedTicketingsMutation,
    useToggleArchiveTicketingsMutation,
    useGetArchivedTicketingExportMutation,
    useGetTicketingExportMutation
} from '@services/member/ticketing/ticketing.endpoints';
import { TicketingTable } from '@services/member/ticketing/ticketing.types';
import { useGetTicketTypesMutation } from '@services/member/list/list.endpoints';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { TicketType } from '@services/member/list/ticket-types/ticket-types.types';
import { openPrintWindow } from '@utils/printUtils';
import { generatePrintHTML } from './ListPagePrint';

export const useListPageVM = () => {
    const MySwal = withReactContent(Swal);

    const [totalMembers, setTotalMembers] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
    const columnHelper = createColumnHelper<TicketingTable>();

    const navigate = useNavigate();
    const [needReload, setNeedReload] = useState<boolean>();

    const [toggleArchiveTicketing, { isSuccess: isToggleArchiveTicketingSuccess, isLoading: isToggleArchiveTicketingLoading }] =
        useToggleArchiveTicketingsMutation();
    const [getTicketTypes, { isSuccess: isGetTicketTypesSuccess, isLoading: isGetTicketTypesLoading, data: ticketTypesData }] =
        useGetTicketTypesMutation();

    const mutationHook = useMemo(() => {
        return isArchived ? useGetArchivedTicketingsMutation : useGetTicketingsMutation;
    }, [isArchived]);

    const exportMutationHook = useMemo(() => {
        return isArchived ? useGetArchivedTicketingExportMutation : useGetTicketingExportMutation;
    }, [isArchived]);

    useEffect(() => {
        getTicketTypes(undefined);
    }, []);

    useEffect(() => {
        if (isGetTicketTypesLoading) return;
        if (isGetTicketTypesSuccess && ticketTypesData) {
            setTicketTypes(ticketTypesData.data);
        }
    }, [isGetTicketTypesSuccess]);

    useEffect(() => {
        setTotalMembers(0);
    }, [isArchived]);

    useEffect(() => {
        if (isToggleArchiveTicketingSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleArchiveTicketingSuccess]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalMembers(metadata.total);
        }
    };

    const handleToggleArchiveTicketing = (id: number, archive: boolean) => {
        MySwal.fire({
            title: archive ? 'Êtes-vous sûr de vouloir archiver ce billet ?' : 'Êtes-vous sûr de vouloir désarchiver ce billet ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                toggleArchiveTicketing({ id, archived: archive });
            }
        });
    };

    const handleToggleArchiveTicketings = (lines: TicketingTable[], archive: boolean) => {
        MySwal.fire({
            title: archive ? 'Êtes-vous sûr de vouloir archiver ces billets ?' : 'Êtes-vous sûr de vouloir désarchiver ces billets ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    toggleArchiveTicketing({ id: line.id, archived: archive });
                });
            }
        });
    };

    const handlePrint = async (lines: TicketingTable[]) => {
        if (!lines || !lines?.length) {
            console.error("Aucune ligne n'est sélectionnée");
            return;
        }

        try {
            const printHtml = generatePrintHTML(lines);
            await openPrintWindow(printHtml, {
                title: `Billets sélectionnés - ${lines.length} billet(s)`,
                width: 800,
                height: 600
            });
        } catch (error) {
            console.error("Erreur lors de l'impression:", error);
        }
    };

    return {
        handleToggleArchiveTicketing,
        handleToggleArchiveTicketings,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        mutationHook,
        setIsArchived,
        totalMembers,
        ticketTypes,
        isLoading: isToggleArchiveTicketingLoading,
        needReload,
        exportMutationHook,
        handlePrint
    };
};
